import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmailTemplate } from '@/types/emailTemplate';

interface TemplatePreviewProps {
  template: EmailTemplate;
  previewData?: {
    intervieweeName?: string;
    question?: string;
    context?: string;
  };
}

const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  template,
  previewData = {
    intervieweeName: 'Navn Navnesen',
    question: 'Hva er konsekvensene av den siste klimarapporten for havnivåstigning i Norge?',
    context: 'Norges klimainstitutt kom nylig med en rapport som viser økning i havnivåstigning.'
  }
}) => {
  const { intervieweeName, question, context } = previewData;
  
  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Forhåndsvisning</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="bg-white border rounded-lg overflow-hidden">
          {/* Email Header */}
          <div className="bg-muted/30 px-4 py-3 border-b">
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">Fra:</span> {template.sender_name} &lt;{template.sender_email}&gt;
            </p>
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">Emne:</span> {template.email_subject || 'Forespørsel om kommentar fra Briefr.no'}
            </p>
          </div>

          {/* Email Body */}
          <div className="p-4 space-y-4 text-sm">
            {/* Logo placeholder */}
            <div className="text-center">
              <div className="inline-block w-16 h-16 bg-muted rounded flex items-center justify-center">
                <span className="text-xs text-muted-foreground">Logo</span>
              </div>
            </div>

            {/* Greeting */}
            <p>Hei {intervieweeName},</p>

            {/* Intro text */}
            <p className="text-muted-foreground">{template.intro_text}</p>

            {/* Question section */}
            <p>Vi ønsker gjerne ditt svar på spørsmålet:</p>
            
            <div className="bg-muted/50 border-l-4 border-primary p-4 space-y-2">
              <p><strong>Spørsmål:</strong> {question}</p>
              <p><strong>Kontekst:</strong> {context || "(ikke lagt til, fremstår åpenbar)"}</p>
            </div>

            {/* CTA text */}
            <p className="text-muted-foreground">{template.call_to_action_text}</p>

            {/* CTA Button */}
            <div className="text-center py-2">
              <span className="inline-block bg-primary text-primary-foreground px-6 py-2 rounded font-medium">
                Svar her
              </span>
            </div>

            {/* Footer note */}
            <p className="text-xs text-muted-foreground">
              Svar gjerne på denne eposten dersom du har spørsmål. Briefr driftes etter Vær Varsom-plakaten og Redaktørplakaten.
            </p>

            {/* Signature */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm">
                Vennlig hilsen,<br />
                <strong>{template.sender_name}</strong>
                {template.sender_title && (
                  <> | {template.sender_title}</>
                )}
                {' '}| <span className="text-primary">briefr.no</span><br />
                E-post: <a href={`mailto:${template.sender_email}`} className="text-primary">{template.sender_email}</a><br />
                {template.sender_phone && <>Tlf: {template.sender_phone}<br /></>}
                Ansvarlig redaktør: {template.editor_name || 'Tom Sekse'}
              </p>

              {/* Logos placeholder */}
              <div className="flex gap-4 justify-center mt-4">
                <div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
                  <span className="text-xs text-muted-foreground">Briefr</span>
                </div>
                <div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
                  <span className="text-xs text-muted-foreground">Redaktør</span>
                </div>
              </div>

              <p className="text-center mt-2">
                <a href="https://www.briefr.no" className="text-primary text-sm">www.briefr.no</a>
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TemplatePreview;
